// src/hooks/useKreatorProgress.js
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { logger } from '@/lib/logger';

/**
 * Hook to fetch and compute Kreator (Branch Setup Wizard) progress
 * Auto-detects completion status from actual database state
 * Supports skipping tasks that have acceptable defaults
 */
export const useKreatorProgress = (branchId, options = {}) => {
  return useQuery({
    queryKey: ['kreator-progress', branchId],
    queryFn: async () => {
      // Use optimized RPC function that bypasses slow RLS policies
      const { data: result, error } = await supabase.rpc('get_kreator_progress_data', {
        p_branch_id: branchId
      });

      if (error) {
        logger.error('Error fetching kreator progress', { message: error.message });
        throw error;
      }

      const branch = result?.branch;
      const children = result?.children || [];
      const closuresCount = result?.closuresCount || 0;
      const costElementsCount = result?.costElementsCount || 0;
      const badgesCount = result?.badgesCount || 0;
      const groupsCount = result?.groupsCount || 0;
      const teachersCount = result?.teachersCount || 0;
      const menusCount = result?.menusCount || 0;

      // Get skipped tasks from branch settings
      const skippedTasks = branch?.settings?.kreator_skipped_tasks || [];

      // Calculate unique parent count from all children's parent_ids
      const parentIds = new Set(children.flatMap(c => c.parent_ids || []));
      const parentCount = parentIds.size;

      // Define all tasks with completion checks
      // canSkip: true for tasks that have acceptable defaults or can be configured later
      const tasks = [
        {
          id: 'branch_name',
          label: 'Nazwa placówki',
          description: 'Ustaw nazwę wyświetlaną',
          actuallyCompleted: !!branch?.display_name?.trim(),
          skipped: skippedTasks.includes('branch_name'),
          canSkip: true,
          link: '/BranchSettings',
          section: 'branchData',
          icon: 'building'
        },
        {
          id: 'logo',
          label: 'Logo placówki',
          description: 'Dodaj logo przedszkola',
          actuallyCompleted: !!branch?.photo_url,
          skipped: skippedTasks.includes('logo'),
          canSkip: true,
          link: '/BranchSettings',
          section: 'branchData',
          icon: 'image'
        },
        {
          id: 'closures',
          label: 'Dni wolne',
          description: 'Dodaj przynajmniej 1 dzień wolny lub święto',
          actuallyCompleted: closuresCount > 0,
          skipped: skippedTasks.includes('closures'),
          canSkip: true,
          link: '/BranchSettings',
          section: 'closures',
          icon: 'calendar-off'
        },
        {
          id: 'cost_elements',
          label: 'Elementy kosztowe',
          description: 'Dodaj min. 2 aktywne elementy (np. czesne, wyżywienie)',
          actuallyCompleted: costElementsCount >= 2,
          skipped: skippedTasks.includes('cost_elements'),
          canSkip: true,
          link: '/BranchSettings',
          section: 'billingSettings',
          icon: 'coins'
        },
        {
          id: 'criteria',
          label: 'Kryteria oceny',
          description: 'Zdefiniuj kryteria oceny dziennej',
          actuallyCompleted: (branch?.daily_update_criteria?.length || 0) > 0,
          skipped: skippedTasks.includes('criteria'),
          canSkip: true,
          link: '/BranchSettings',
          section: 'criteria',
          icon: 'clipboard-check'
        },
        {
          id: 'badges',
          label: 'Odznaki',
          description: 'Włącz przynajmniej 1 odznakę',
          actuallyCompleted: badgesCount > 0,
          skipped: skippedTasks.includes('badges'),
          canSkip: true,
          link: '/BranchSettings',
          section: 'badges',
          icon: 'award'
        },
        {
          id: 'meal_structure',
          label: 'Struktura posiłków',
          description: 'Skonfiguruj typy posiłków serwowanych w placówce',
          actuallyCompleted: (branch?.settings?.menu_structure_details?.length || 0) > 0,
          skipped: skippedTasks.includes('meal_structure'),
          canSkip: true,
          link: '/BranchSettings',
          section: 'meals',
          icon: 'utensils'
        },
        {
          id: 'groups_teachers',
          label: 'Grupy i nauczyciele',
          description: 'Utwórz grupę i zaproś nauczyciela',
          actuallyCompleted: groupsCount > 0 && teachersCount > 0,
          skipped: skippedTasks.includes('groups_teachers'),
          canSkip: true,
          link: '/Groups',
          section: null,
          icon: 'users'
        },
        {
          id: 'children_parents',
          label: 'Dzieci i rodzice',
          description: 'Dodaj dziecko i zaproś rodzica',
          actuallyCompleted: children.length > 0 && parentCount > 0,
          skipped: skippedTasks.includes('children_parents'),
          canSkip: true,
          link: '/Children',
          section: null,
          icon: 'baby'
        },
        {
          id: 'menus',
          label: 'Jadłospis',
          description: 'Utwórz lub zaimportuj jadłospis',
          actuallyCompleted: menusCount > 0,
          skipped: skippedTasks.includes('menus'),
          canSkip: true,
          link: '/MenuManagement',
          section: null,
          icon: 'utensils-crossed'
        }
      ].map(task => ({
        ...task,
        // Task is considered complete if actually completed OR skipped
        completed: task.actuallyCompleted || task.skipped
      }));

      const completedCount = tasks.filter(t => t.completed).length;
      const totalCount = tasks.length;
      const isAllComplete = completedCount === totalCount;

      // Debug logging for troubleshooting Kreator issues
      if (!isAllComplete) {
        const incompleteTasks = tasks.filter(t => !t.completed);
        logger.info('Kreator incomplete tasks', {
          branchId,
          incompleteTasks: incompleteTasks.map(t => ({
            id: t.id,
            label: t.label,
            actuallyCompleted: t.actuallyCompleted,
            skipped: t.skipped
          })),
          skippedTasks
        });
      }

      return {
        tasks,
        completedCount,
        totalCount,
        progressPercent: Math.round((completedCount / totalCount) * 100),
        isAllComplete,
        skippedTasks
      };
    },
    enabled: !!branchId,
    staleTime: 30 * 1000, // 30 seconds
    refetchOnWindowFocus: true,
    ...options
  });
};

/**
 * Mutation hook to skip or unskip a Kreator task
 */
export const useSkipKreatorTask = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ branchId, taskId, skip }) => {
      // First get current settings
      const { data: branch, error: fetchError } = await supabase
        .from('branches')
        .select('settings')
        .eq('id', branchId)
        .single();

      if (fetchError) throw fetchError;

      const currentSettings = branch?.settings || {};
      const currentSkipped = currentSettings.kreator_skipped_tasks || [];

      // Add or remove taskId from skipped list
      let newSkipped;
      if (skip) {
        newSkipped = [...new Set([...currentSkipped, taskId])];
      } else {
        newSkipped = currentSkipped.filter(id => id !== taskId);
      }

      // Update settings
      const { error: updateError } = await supabase
        .from('branches')
        .update({
          settings: {
            ...currentSettings,
            kreator_skipped_tasks: newSkipped
          }
        })
        .eq('id', branchId);

      if (updateError) throw updateError;

      return { branchId, taskId, skip };
    },
    onSuccess: ({ branchId }) => {
      // Invalidate kreator progress query
      queryClient.invalidateQueries({ queryKey: ['kreator-progress', branchId] });
    }
  });
};
